<script setup lang="ts">
import Badge from '../Common/Badge/Badge.vue'
import type { MagazineArticle, MagazineArticleVote } from '~/types/magazine'
import Avatar from '~/components/Avatar.vue'
import { useMagazineArticlesStore } from '~/stores/magazine/Articles'
import { useMagazineCategoriesStore } from '~/stores/magazine/Categories'
import { formatDate } from '~/utils/Date'
import type { Profile } from '~/types/profile'
import type { ToastNotification } from '~/components/Toast.vue'
import Search from '~/components/Magazine/Preview/Search.vue'
import Micro from '~/components/Magazine/Preview/Micro.vue'

const { $navigate } = useNuxtApp()

const props = defineProps<{
	article: MagazineArticle
	theme?: 'slim-profile' | 'full' | 'related' | 'search' | 'micro'
	isLoading?: boolean
	showTypeTag?: boolean
	closeOnSelect?: boolean
}>()

const emits = defineEmits(['selected', 'close'])

const localArticle = ref<MagazineArticle>(props.article)

const isAuthed = inject('isAuthed') as Ref<boolean>
const profile = inject('profile') as Ref<Profile>
const toast = inject('toast') as Ref<ToastNotification>
const router = useRouter()

const categoryStore = useMagazineCategoriesStore()
const articleStore = useMagazineArticlesStore(props.article.category_id)

function stripHtml(html: string) {
	return html.replace(/<\/?[^>]+>/gi, '')
}

const slug = await categoryStore().getCategorySlug(props.article.category_id)

const upvoted = computed(
	() => !!localArticle.value?.magazine_articles_upvotes?.find((v: MagazineArticleVote) => v.user_id === profile?.value?.id),
)
const downvoted = computed(
	() => !!localArticle.value?.magazine_articles_downvotes?.find((v: MagazineArticleVote) => v.user_id === profile?.value?.id),
)

// Share URLs
const url = useRequestURL()
const pageUrl = `${url.origin}/${slug}`
const pageTitle = `${props.article.title} - Article - Health Nutrition`

function copyLink() {
	navigator.clipboard.writeText(pageUrl)

	toast.value = {
		show: true,
		timeout: 3,
		title: 'Link Copied',
		message: 'Discussion link has been copied to clipboard',
		type: 'success',
	}
}

async function upvote() {
	if (!isAuthed.value) {
		const title = 'Login To Upvote'
		const message = `Please login (or register for free) to upvote this magazine article. Once completed, you will be redirected back to this magazine article.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try {
			await articleStore().upvote({
				article_id: props.article.id,
				user_id: profile?.value?.id,
			})
			localArticle.value = await articleStore().get(props.article.id)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Article upvoted',
				message: 'You have successfully upvoted this article.',
				type: 'success',
			}
		}catch (error) {
			console.error('Error upvoting article:', error)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error upvoting article',
				message: 'There was an error upvoting this article. Please try again.',
				type: 'error',
			}
		}
	}

}

async function removeUpvote() {
	if (!isAuthed.value) {
		const title = 'Login To Remove Upvote'
		const message = `Please login (or register for free) to remove your upvote from this magazine article. Once completed, you will be redirected back to this magazine article.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try{
			await articleStore().removeUpvote({
				article_id: props.article.id,
				user_id: profile?.value?.id
			})
			localArticle.value = await articleStore().get(props.article.id)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Upvote removed',
				message: 'You have successfully removed your upvote.',
				type: 'success',
			}
		}catch (error) {
			console.error('Error removing upvote:', error)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error removing upvote',
				message: 'There was an error removing your upvote. Please try again.',
				type: 'error',
			}
		}
	}
}

async function downvote() {
	if (!isAuthed.value) {
		const title = 'Login To Downvote'
		const message = `Please login (or register for free) to downvote this magazine article. Once completed, you will be redirected back to this magazine article.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try{
			await articleStore().downvote({
				article_id: props.article.id,
				user_id: profile?.value?.id
			})
			localArticle.value = await articleStore().get(props.article.id)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Article downvoted',
				message: 'You have successfully downvoted this article.',
				type: 'success',
			}
		} catch (error) {
			console.error('Error downvoting article:', error)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error downvoting article',
				message: 'There was an error downvoting this article. Please try again.',
				type: 'error',
			}
		}
	}
}

async function removeDownvote() {
	if (!isAuthed.value) {
		const title = 'Login To Remove Downvote'
		const message = `Please login (or register for free) to remove your downvote from this magazine article. Once completed, you will be redirected back to this magazine article.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try{
			await articleStore().removeDownvote({
				article_id: props.article.id,
				user_id: profile?.value?.id,
			})
			localArticle.value = await articleStore().get(props.article.id)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Downvote removed',
				message: 'You have successfully removed your downvote.',
				type: 'success',
			}
		} catch (error) {
			console.error('Error removing downvote:', error)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error removing downvote',
				message: 'There was an error removing your downvote. Please try again.',
				type: 'error',
			}
		}
	}
}

function clicked(){

	if(props.closeOnSelect){
		emits('close')
	}

	$navigate(`/magazine/${slug}/article/${localArticle.value.handle}`)

}

</script>

<template>

	<Search v-if="theme === 'search'" :article="localArticle" :showTypeTag="showTypeTag" @selected="clicked"/>

	<Micro v-else-if="theme === 'micro'" :article="localArticle" @selected="clicked"/>

	<div v-else
		:class="[theme === 'related' ? 'hover:bg-green-50' : 'hover:ring-1 hover:ring-green-400' ,'card relative no-underline']">
		<!-- OVERLAY -->
		<NuxtLink @click="clicked" class="absolute inset-0 cursor-pointer" />
		<div
			v-if="theme === 'slim-profile'"
			class="flex flex-row items-center bg-white hover:bg-gray-50 cursor-pointer">
			<div class="flex p2 py-4 flex-col basis-1/12 border-r border-gray-100 p-2 text-center items-center">
				<SkeletonLoader v-if="isLoading" class="h-8 w-8 rounded-full" />
				<Icon v-else name="ph:newspaper-duotone" class="size-7 flex-none text-gray-400" />
			</div>
			<div class="flex flex-col grow basis-8/12 sm:basis-9/12 p-2 sm:px-4">
				<SkeletonLoader v-if="isLoading" class="h-8 w-3/4 mb-2" />
				<h2 v-else class="text-sm sm:text-base font-bold text-green-500 my-0">{{ localArticle.title }}</h2>
				<SkeletonLoader v-if="isLoading" class="h-4 w-1/2" />
				<span v-else class="text-xs text-gray-400">{{ ago(localArticle.created_at) }}</span>
			</div>
			<div class="flex flex-col text-center justify-center items-center basis-1/12 border-l border-gray-100 p-2">
				<SkeletonLoader v-if="isLoading" class="h-8 w-8 rounded-full" />
				<Icon v-else name="ph:chat-duotone" class="size-5 flex-none text-gray-500" aria-hidden="true" />
				<SkeletonLoader v-if="isLoading" class="h-4 w-4" />
				<span v-else class="text-sm text-black">{{ localArticle.comment_count }}</span>
			</div>
		</div>

		<div v-else-if="theme === 'related'" class="flex flex-col">
			<div v-if="localArticle.image_url" class="aspect-w-16 aspect-h-9 mb-2">
				<SkeletonLoader v-if="isLoading" class="aspect-w-16 aspect-h-9" />
				<img v-else :src="localArticle.image_url" :alt="localArticle.title" class="object-cover w-full max-h-36 rounded-t-xl" />
			</div>
			<div class="px-4">
				<SkeletonLoader v-if="isLoading" class="h-8 w-3/4 mb-2" />
				<h4 v-else class="text-lg">{{ localArticle.title }}</h4>
			</div>
		</div>

		<div v-else>
			<div v-if="localArticle.image_url" class="aspect-w-16 aspect-h-9 mb-2">
				<SkeletonLoader v-if="isLoading" class="aspect-w-16 aspect-h-9" />
				<img v-else :src="localArticle.image_url" :alt="localArticle.title" class="object-cover w-full h-full rounded-t-xl" />
			</div>
			<div class="px-4 py-2 pt-0">
				<div class="flex items-center gap-2">
					<div v-if="localArticle.users?.length" class="flex items-center w-16 h-16 mr-2 z-2 relative">
						<SkeletonLoader v-if="isLoading" class="h-16 w-16 rounded-full" />
						<Avatar
							v-if="!isLoading && localArticle.users[0]"
							:profile="localArticle.users[0]"
							:size="10"
							type="rounded" />
					</div>

					<SkeletonLoader v-if="isLoading" class="h-8 w-3/4 mb-2" />
					<h3 v-else class="text-xl font-semibold text-gray-900 line-clamp-2">
						{{ localArticle.title }}
					</h3>
				</div>

				<div class="mt-2 flex items-center gap-2 text-base text-gray-500">
					<SkeletonLoader v-if="isLoading" class="h-4 w-1/4" />
					<div v-else class="flex items-center gap-1">
						<span>{{ localArticle.users?.[0].username }}</span>
					</div>
					<span>•</span>
					<SkeletonLoader v-if="isLoading" class="h-4 w-1/4" />
					<time v-else :datetime="formatDate(localArticle.created_at)">
						{{ formatDate(localArticle.created_at) }}
					</time>
				</div>

				<SkeletonLoader v-if="isLoading" class="h-4 w-full mt-2" />
				<p v-else class="mt-2 text-gray-500 text-sm sm:text-base line-clamp-3">
					{{ stripHtml(localArticle.content) }}
				</p>

				<div class="flex flex-row items-center border-t border-gray-100">
					<div class="flex flex-row items-center gap-4 flex-1">
						<div v-if="localArticle?.users?.[0].id !== profile?.id" class="flex flex-row gap-1">
							<!-- Upvote -->
							<NuxtLink
								v-if="!upvoted && !downvoted"
								class="flex items-center justify-center font-semibold text-gray-900"
								@click="upvote">
								<Icon
									name="ph:thumbs-up-duotone"
									class="size-5 flex-none text-gray-500 hover:text-green-500"
									aria-hidden="true" />
							</NuxtLink>
							<NuxtLink
								v-else-if="upvoted && !downvoted"
								class="flex items-center justify-center font-semibold text-gray-900"
								@click="removeUpvote">
								<Icon name="ph:thumbs-up-fill" class="size-5 flex-none text-gray-500 hover:text-green-500" aria-hidden="true" />
							</NuxtLink>
							<div v-else class="flex items-center justify-center font-semibold text-gray-400">
								<Icon
									name="ph:thumbs-up-duotone"
									class="size-5 flex-none text-gray-400"
									aria-hidden="true" />
							</div>

							<!-- Downvote -->
							<NuxtLink
								v-if="!upvoted && !downvoted"
								class="flex items-center justify-center font-semibold text-gray-900"
								@click="downvote">
								<Icon
									name="ph:thumbs-down-duotone"
									class="size-5 flex-none text-gray-500 hover:text-green-500"
									aria-hidden="true" />
							</NuxtLink>
							<NuxtLink
								v-else-if="!upvoted && downvoted"
								class="flex items-center justify-center font-semibold text-gray-900 hover:text-green-500"
								@click="removeDownvote">
								<Icon
									name="ph:thumbs-down-fill"
									class="size-5 flex-none text-gray-500"
									aria-hidden="true" />
							</NuxtLink>
							<div v-else class="flex items-center justify-center font-semibold text-gray-400">
								<Icon
									name="ph:thumbs-down-duotone"
									class="size-5 flex-none text-gray-400"
									aria-hidden="true" />
							</div>
							<span class="text-sm">{{
								(localArticle.magazine_articles_upvotes?.length ?? 0) - (localArticle.magazine_articles_downvotes?.length ?? 0)
							}}</span>
						</div>

						<div v-if="localArticle?.users?.[0].id === profile?.id" class="flex flex-row gap-1">
							<Icon name="ph:thumbs-up-duotone" class="size-5 flex-none text-gray-400" aria-hidden="true" />
							<span class="text-sm">{{ localArticle.votes }}</span>
						</div>

						<!-- Comment -->
						<div class="flex items-center gap-1">
							<Icon name="ph:chat-duotone" class="size-5 flex-none text-gray-500" aria-hidden="true" />
							<span class="text-sm">{{ localArticle.comment_count }}</span>
						</div>

						<Share :title="pageTitle" :url="pageUrl" @copied="copyLink" />
					</div>

					<div class="flex flex-row items-center gap-0 px-0 lg:px-2 pt-2 pb-1 ">
						<a
							class="text-gray-700 block text-sm no-underline"
							@click="() => copyLink()">
							<Icon name="ph:link-duotone" class="size-5 icon-fix  hover:text-green-500 "
						/></a>

						<Badge v-if="showTypeTag" color="yellow" class="ml-2">MAGAZINE</Badge>
					</div>
				</div>
			</div>

		</div>
	</div>
</template>
